import React, { useMemo } from "react";
import { RAIL } from "../../components/terminal/RailTag.jsx";
import { fmtAbbr, fmtCcy } from "../../components/terminal/format.js";

/* 92px settlement-timeline strip across the top of Overview.
   Pins events at -2..+72h with rail-colored dots and a "NOW" line.
   Data shape matches BACKEND_TERMINAL_SPEC A.3 verbatim. */

export default function Timeline({ events = [] }) {
  const T_MIN = -2, T_MAX = 72;
  const xPct = (t) => ((t - T_MIN) / (T_MAX - T_MIN)) * 100;
  const ticks = [0, 6, 12, 24, 36, 48, 60, 72];

  const totals = useMemo(() => {
    let inn = 0, out = 0, count = 0;
    events.forEach((e) => {
      if (e.t_offset_hours < 0) return;
      count++;
      const v = parseFloat(e.amount) || 0;
      if (e.direction === "in") inn += v;
      else if (e.direction === "out") out += v;
    });
    return { inn, out, count };
  }, [events]);

  return (
    <div style={{ position:"relative", zIndex:1, height:92, borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)", overflow:"hidden" }}>
      <div style={{ position:"absolute", left:14, top:8, fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>
        SETTLEMENT TIMELINE · NEXT 72 HOURS
      </div>
      <div style={{ position:"absolute", right:14, top:8, display:"flex", gap:22, fontSize:10, color:"var(--term-ink-3)", letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:600 }}>
        <span>EVENTS<span className="mono" style={{ color:"var(--term-ink-1)", fontWeight:500, marginLeft:8, fontSize:11.5 }}>{totals.count}</span></span>
        <span>INFLOWS<span className="mono" style={{ color:"var(--term-green)", fontWeight:500, marginLeft:8, fontSize:11.5 }}>+{fmtAbbr(totals.inn)}</span></span>
        <span>OUTFLOWS<span className="mono" style={{ color:"var(--term-red)", fontWeight:500, marginLeft:8, fontSize:11.5 }}>−{fmtAbbr(totals.out)}</span></span>
      </div>
      <div style={{ position:"absolute", left:0, right:0, top:32, bottom:0 }}>
        <div style={{ position:"absolute", left:14, right:14, bottom:18, height:1, background:"var(--term-line)" }} />

        {ticks.map((t) => (
          <div key={t} style={{ position:"absolute", bottom:18, transform:"translateX(-50%)", left:`calc(14px + ${xPct(t)}% * (100% - 28px) / 100%)` }}>
            <div style={{ width:1, height:5, background:"var(--term-ink-4)", margin:"0 auto" }} />
            <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.08em", paddingTop:3, textAlign:"center", whiteSpace:"nowrap" }}>
              {t === 0 ? "NOW" : `+${t}h`}
            </div>
          </div>
        ))}

        {/* NOW line */}
        <div style={{ position:"absolute", top:0, bottom:18, width:1, background:"var(--term-green)", boxShadow:"0 0 10px var(--term-green)", left:`calc(14px + ${xPct(0)}% * (100% - 28px) / 100%)` }}>
          <div className="mono" style={{ position:"absolute", top:2, transform:"translateX(4px)", fontSize:9.5, color:"var(--term-green)", letterSpacing:"0.10em" }}>NOW</div>
        </div>

        {events.map((e) => {
          const t = e.t_offset_hours;
          const rail = RAIL[e.rail] || { color:"var(--term-ink-2)", label:e.rail };
          const dirAmt = e.direction === "in" ? "+" : e.direction === "out" ? "−" : "⇄";
          const v = parseFloat(e.amount) || 0;
          const pinH = Math.max(20, Math.min(54, 18 + Math.log10(Math.max(v, 1)) * 6));
          return (
            <div
              key={e.id}
              title={`${dirAmt} ${fmtCcy(v, e.currency)} · ${e.counterparty}`}
              style={{
                position:"absolute", bottom:18, transform:"translateX(-50%)",
                display:"flex", flexDirection:"column", alignItems:"center",
                cursor:"pointer", color: rail.color,
                left:`calc(14px + ${xPct(t)}% * (100% - 28px) / 100%)`,
                height: pinH + 8,
              }}
            >
              <div style={{ width:1, background:"currentColor", opacity:0.55, height: pinH }} />
              <div style={{ width:7, height:7, borderRadius:"50%", background:"currentColor", boxShadow:"0 0 8px currentColor", marginTop:-3, flexShrink:0 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
